import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useSearchParams } from 'react-router-dom';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import classNames from 'classnames';
import ListMapViewTabs from 'src/shared/components/list-map-view-tabs/ListMapViewTabs';
import PortfolioFilters from 'src/app/portfolio/components/portfolio-filters/PortfolioFilters';
import PortfolioSiteExplorerMapSidebar from './components/map-sidebar/PortfolioSiteExplorerMapSidebar';
import PortfolioSiteExplorerMapMain from './components/map-main/PortfolioSiteExplorerMapMain';
import PortfolioSiteExplorerListView from './components/list-view/PortfolioSiteExplorerListView';
import { getPortfolioSiteExplorerState } from './state/portfolioSiteExplorerSelector';
import { updateUiState } from './state/portfolioSiteExplorerSlice';
import styles from './PortfolioSiteExplorer.module.scss';
import usePortfolioSiteExplorerAnalytics from './hooks/usePortfolioSiteExplorerAnalytics';
import PortfolioActiveFilters from '../portfolio-summary/components/PortfolioActiveFilters/PortfolioActiveFilters';

export const PortfolioSiteExplorer = () => {
    const dispatch = useAppDispatch();
    const uiState = useAppSelector(getPortfolioSiteExplorerState);
    const { isSidebarOpen, view } = uiState;
    const [searchParams] = useSearchParams();
    const { trackListViewTabClick, trackMapViewTabClick } = usePortfolioSiteExplorerAnalytics();

    // Check for URL parameters to determine initial view
    const viewParam = searchParams.get('view');

    // Convert string parameters to proper enum types
    const initialView = viewParam as 'map' | 'list' | null;

    // Auto-switch to list view when metric parameters are present or when view parameter is set
    useEffect(() => {
        if (initialView && view !== initialView) {
            dispatch(updateUiState({ view: initialView }));
        }
    }, [initialView, view, dispatch]);

    const isMapView = view === 'map';

    const mainContentClassName = classNames(styles.mainContent, {
        [styles.mainContentExpanded]: !isSidebarOpen || !isMapView,
    });
    const sidebarClassName = classNames(styles.sidebar, {
        [styles.sidebarClosed]: !isSidebarOpen,
    });

    const onSidebarToggle = () => {
        dispatch(updateUiState({ isSidebarOpen: !isSidebarOpen }));
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 500);
    };

    const handleTabChange = (tab: 'map' | 'list') => {
        dispatch(updateUiState({ view: tab }));

        // Track analytics for tab clicks
        if (tab === 'list') {
            trackListViewTabClick();
        } else if (tab === 'map') {
            trackMapViewTabClick();
        }
    };

    return (
        <StandardAppLayout
            classNames={{
                mainContent: styles.mainContentOverride,
                topContent: '!pt-2 !pb-0',
                otherActions: 'p-0 pt-1 justify-normal',
            }}
            title='Portfolio site explorer'
            mainContent={
                <div className={styles.portfolioMapContainer}>
                    <div className='mb-2 mx-8'>
                        <PortfolioActiveFilters />
                    </div>
                    <div className={styles.headerRow}>
                        <div className='self-end'>
                            <ListMapViewTabs tab={view || 'map'} onTabChange={handleTabChange} isMapFirst />
                        </div>
                    </div>

                    <div className={styles.portfolioMapContent}>
                        <section className={mainContentClassName}>
                            {isMapView ? <PortfolioSiteExplorerMapMain /> : <PortfolioSiteExplorerListView />}
                        </section>
                        {isMapView && (
                            <section className={sidebarClassName}>
                                <PortfolioSiteExplorerMapSidebar isSidebarOpen={isSidebarOpen} setSidebarOpen={onSidebarToggle} />
                            </section>
                        )}
                    </div>
                </div>
            }
            lozenges={<PortfolioFilters />}
        />
    );
};

export default PortfolioSiteExplorer;
